import { generatePath } from 'react-router';
import type { AxiosResponse } from 'axios';
import type {
    IBsiPopupResponseCreateRequest,
    IBsiPopupResponses,
    IBsiPopupResponsesFetchRequest,
    IProcessInstance,
    IProcessTaskRequest,
    IStartProcessRequest,
} from '../../domain';
import { ApiBaseService } from '../api-base-service';

export const Endpoint = {
    ALL_INSTANCES: '/instances',
    INSTANCES: '/instances/:id',
    POPUPS_FETCH: '/popups?kdLnr=:kdLnr',
    POPUP_RESPONSE: '/popups',
    PROCESSES: '/processes',
    TASKS: '/tasks/:id',
};

export const BSI_PROCESS_NAME = 'BsiRequest';

const extractLastPartOfLocation = (location: string) => location?.split('/').slice(-1).pop();

export class ProcessEngineApiService extends ApiBaseService {
    constructor(context = '/delegate/process-engine/v1') {
        super(context);
    }

    async getAllInstances(): Promise<IProcessInstance[]> {
        const { data } = await this.http.get<IProcessInstance[]>(Endpoint.ALL_INSTANCES);

        return data;
    }

    async getInstance(id: string): Promise<IProcessInstance> {
        const { data } = await this.http.get<IProcessInstance>(generatePath(Endpoint.INSTANCES, { id }));

        return data;
    }

    async getPopupResponses(payload: IBsiPopupResponsesFetchRequest): Promise<IBsiPopupResponses[]> {
        const { data } = await this.http.get<IBsiPopupResponses[]>(generatePath(Endpoint.POPUPS_FETCH, { kdLnr: payload.kdLnr }));

        return data;
    }

    async postPopupResponse(data: IBsiPopupResponseCreateRequest): Promise<{ headers: AxiosResponse['headers'] }> {
        const { headers } = await this.http.post(generatePath(Endpoint.POPUP_RESPONSE, { kdLnr: data.kdLnr }), data);

        return headers;
    }

    async processTask({ taskId: id, task }: IProcessTaskRequest): Promise<void> {
        const { data } = await this.http.put(generatePath(Endpoint.TASKS, { id }), task);

        return data;
    }

    async startBsiProcess(externalId?: string): Promise<string> {
        const request: IStartProcessRequest = {
            name: BSI_PROCESS_NAME,
        };
        if (externalId) {
            request.externalId = externalId;
        }

        return this.startProcess(request);
    }

    async startProcess(params: IStartProcessRequest): Promise<string> {
        const { headers } = await this.http.post(Endpoint.PROCESSES, params);
        const instanceId = extractLastPartOfLocation(headers['location']);
        if (!instanceId) {
            throw new Error('the start process endpoint did not return a location header');
        }

        return instanceId;
    }
}
